import {
  DEFAULT_MATCHING_PRIORITIES,
  JobOffer,
  MatchingPriorities,
  Skill,
} from '../../../models/job';
import { JobOfferUpdateData, JobRescoreSummary } from '../../../types/job.types';

export type EditFormData = Record<string, string | Skill[] | MatchingPriorities>;

const toSkills = (job: JobOffer): Skill[] => {
  const raw = job.skills?.length
    ? job.skills
    : [...(job.codingLanguages || []), ...(job.frameworksLibraries || [])];
  return raw
    .map((entry) =>
      typeof entry === 'string' ? { name: entry, importance: 'uncertain' as const } : entry
    )
    .filter((skill): skill is Skill => Boolean(skill?.name));
};

export const formFromJob = (job: JobOffer): EditFormData => ({
  'Job Title': job.title ?? '',
  Location: job.location ?? '',
  'Contract Type': job.contractType ?? '',
  'Remote Policy': job.remotePolicy ?? '',
  'Years of Experience': job.yearsOfExperience != null ? String(job.yearsOfExperience) : '',
  'Required Degree': job.requiredDegree ?? '',
  'Role Description': job.mission_role_in_job ?? '',
  Skills: toSkills(job),
  'Company Name': job.companyName ?? '',
  'Company Sector': job.companySector ?? '',
  'Company Description': job.companyDescription ?? '',
  'Matching Priorities': job.matchingPriorities ?? { ...DEFAULT_MATCHING_PRIORITIES },
});

const textOrNull = (value: string): string | null => value.trim() || null;
const intOrNull = (value: string): number | null => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Display-field → API key. Only changed fields are sent; empty → null. */
const PAYLOAD_MAP: ReadonlyArray<{
  field: string;
  key: keyof JobOfferUpdateData;
  map: (value: string | Skill[] | MatchingPriorities) => JobOfferUpdateData[keyof JobOfferUpdateData];
}> = [
  { field: 'Job Title', key: 'title', map: (v) => String(v).trim() },
  { field: 'Location', key: 'location', map: (v) => textOrNull(String(v)) },
  { field: 'Contract Type', key: 'contract_type', map: (v) => textOrNull(String(v)) },
  { field: 'Remote Policy', key: 'remote_policy', map: (v) => textOrNull(String(v)) },
  { field: 'Years of Experience', key: 'years_of_experience', map: (v) => intOrNull(String(v)) },
  { field: 'Required Degree', key: 'required_degree', map: (v) => textOrNull(String(v)) },
  { field: 'Role Description', key: 'mission_role_in_job', map: (v) => textOrNull(String(v)) },
  { field: 'Skills', key: 'skills', map: (v) => v as Skill[] },
  { field: 'Company Name', key: 'company_name', map: (v) => textOrNull(String(v)) },
  { field: 'Company Sector', key: 'company_sector', map: (v) => textOrNull(String(v)) },
  { field: 'Company Description', key: 'company_description', map: (v) => textOrNull(String(v)) },
  { field: 'Matching Priorities', key: 'matching_priorities', map: (v) => v as MatchingPriorities },
];

export const buildUpdatePayload = (
  initial: EditFormData,
  form: EditFormData
): JobOfferUpdateData => {
  const payload: JobOfferUpdateData = {};
  for (const { field, key, map } of PAYLOAD_MAP) {
    if (JSON.stringify(initial[field]) === JSON.stringify(form[field])) continue;
    (payload as Record<string, unknown>)[key] = map(form[field]!);
  }
  return payload;
};

/** Narrow i18next `t` — do not type as `TFunction`; overloads recurse. */
type TranslateKey = (key: string, options?: Record<string, string | number>) => string;

export const saveToastMessage = (t: unknown, rescore: JobRescoreSummary): string => {
  const translate = t as TranslateKey;
  const exam = Boolean(rescore.exam_regeneration_scheduled);
  const rescoring =
    Boolean(rescore.rescore_scheduled) || (rescore.candidates_to_rescore ?? 0) > 0;
  const count = rescore.candidates_to_rescore ?? 0;
  if (exam && rescoring) return translate('edit_job_modal.saved_exam_and_rescore', { count });
  if (exam) return translate('edit_job_modal.saved_exam_regen');
  if (rescoring) return translate('edit_job_modal.saved_rescoring', { count });
  return translate('edit_job_modal.saved');
};
